import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from '@tanstack/react-router'
import { Command as CommandPrimitive } from 'cmdk'
import { toast } from 'sonner'
import {
  ArrowRight,
  FileText,
  Globe,
  MessageSquareText,
  Plus,
  Search,
  Sparkles,
  StickyNote,
  X,
} from 'lucide-react'
import { HighlightedText } from './HighlightedText'
import { SearchPreview } from './SearchPreview'
import { SearchTextResults } from './SearchTextResults'
import { SEARCH_GROUP_CLASS, SEARCH_ROW_CLASS } from './searchStyles'
import { useSearch } from './useSearch'
import { GO_TO_PLACES } from './goToPlaces'
import { noteHref, pageHref, projectHref, taskHref } from './entityHref'
import type { ComponentType, KeyboardEvent, ReactNode } from 'react'
import type { TextTarget } from './SearchTextResults'
import type { SearchFilter, SearchItem, SearchItemType } from './rankSearch'
import type { FilterTarget } from '@/store/ActiveFilterTargetStore'
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { StatusIcon } from '@/components/app/indicators/StatusIcon'
import { ProjectIcon } from '@/lib/utils/projectIcon'
import { getPlatformShortcutKeys } from '@/lib/utils/keyboard'
import { cn } from '@/lib/utils'
import useLoginStore from '@/store/LoginStore'
import { useAiAvailable } from '@/lib/ai/useAiAvailable'
import { usePages } from '@/lib/db/rxdb/hooks'
import useActiveFilterTargetStore from '@/store/ActiveFilterTargetStore'
import { TypedFilterItems, useTypedFilter } from '@/components/features/filters/TypedFilterItems'

interface SearchDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** ⌘↵ / "Create task "…"": opens the create dialog with the query as title. */
  onCreateTask?: (name: string) => void
  /** "Ask": opens Ask your workspace with the query. */
  onAsk?: (question: string) => void
}

const FILTERS: Array<SearchFilter> = ['all', 'tasks', 'projects', 'pages', 'notes']
const TYPE_FILTER: Record<SearchItemType, SearchFilter> = {
  task: 'tasks',
  project: 'projects',
  page: 'pages',
  note: 'notes',
}
const RECENT_LIMIT = 5


function Kbd({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <kbd
      className={cn(
        'inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-[4px] border border-b-2 border-input bg-card px-1 font-mono text-[10px] font-normal text-muted-foreground',
        className,
        'phone:hidden',
      )}
    >
      {children}
    </kbd>
  )
}

function SearchItemIcon({ item }: { item: SearchItem }) {
  switch (item.type) {
    case 'task':
      return (
        <span className="flex size-4 shrink-0 items-center justify-center">
          <StatusIcon progressType={item.progressType ?? 'BL'} statusId={item.statusId ?? undefined} />
        </span>
      )
    case 'project':
      return (
        <ProjectIcon
          variant="badge"
          iconType={item.iconType}
          icon={item.icon}
          iconColor={item.iconColor}
          fallbackLabel={item.name}
          size={12}
        />
      )
    case 'page':
      return item.icon ? (
        <ProjectIcon iconType={item.iconType} icon={item.icon} iconColor={item.iconColor} size={16} />
      ) : (
        <FileText className="size-4 shrink-0 text-muted-foreground" />
      )
    case 'note':
      return <StickyNote className="size-4 shrink-0 text-muted-foreground" />
  }
}

/**
 * ⌘K takes filter syntax too (design 9b): `status:progress @me` filters the
 * task view on screen instead of searching.
 */
function FilterThisViewGroup({
  query,
  target,
  onDone,
}: {
  query: string
  target: FilterTarget
  onDone: () => void
}) {
  const { t } = useTranslation('tasks')
  const typed = useTypedFilter(query, target)
  if (!typed.parsed) return null
  return (
    <TypedFilterItems
      parsed={typed.parsed}
      heading={t('filters.typed.cmdkHeading')}
      onApply={(filters) => {
        typed.apply(filters)
        onDone()
      }}
    />
  )
}

/**
 * ⌘K (designs 29b, 29c): search scoped to the space you're in, grouped by
 * type with IDs and status on tasks, a preview of the selected result, and
 * actions last so ↵ never creates by accident. Empty, it offers recent items
 * and places to go; with no match, it offers the next thing to try.
 */
export function SearchDialog({ open, onOpenChange, onCreateTask, onAsk }: SearchDialogProps) {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const workspaceSlug = useLoginStore((s) => s.workspaceSlug)
  const inputRef = useRef<HTMLInputElement>(null)
  const aiAvailable = useAiAvailable()
  const filterTarget = useActiveFilterTargetStore((s) => s.target)
  const { pages } = usePages()

  const {
    query,
    setQuery,
    isSettled,
    activeFilter,
    setActiveFilter,
    scope,
    setScope,
    groups,
    counts,
    recentItems,
    addRecentItem,
    reset,
    projectMap,
    statusMap,
    tasks,
  } = useSearch()

  /** Titles, or "in comments and doc text". */
  const [textMode, setTextMode] = useState(false)
  const [textCount, setTextCount] = useState(0)
  const [activeValue, setActiveValue] = useState('')

  useEffect(() => {
    if (!open) {
      reset()
      setTextMode(false)
      setActiveValue('')
    }
  }, [open, reset])

  const trimmed = query.trim()
  const isCommandMode = query.startsWith('>')
  const commandQuery = isCommandMode ? query.slice(1).trim().toLowerCase() : ''
  const hasQuery = trimmed.length > 0 && !isCommandMode
  const scopeLabel = scope?.name ?? ''

  const hrefFor = useCallback(
    (item: SearchItem): string | null => {
      if (!workspaceSlug) return null
      switch (item.type) {
        case 'task':
          return taskHref(workspaceSlug, item.id)
        case 'project': {
          const project = projectMap.get(item.id)
          return project ? projectHref(workspaceSlug, project) : null
        }
        case 'page': {
          const project = item.projectId ? projectMap.get(item.projectId) : undefined
          return project && item.slug ? pageHref(workspaceSlug, project, item.slug) : null
        }
        case 'note':
          return item.noteType && item.noteDate
            ? noteHref(workspaceSlug, item.noteType, item.noteDate)
            : null
      }
    },
    [workspaceSlug, projectMap],
  )

  const close = useCallback(() => onOpenChange(false), [onOpenChange])

  const go = useCallback(
    (href: string) => {
      close()
      void navigate({ to: href })
    },
    [close, navigate],
  )

  const openItem = useCallback(
    (item: SearchItem) => {
      const href = hrefFor(item)
      if (!href) return
      addRecentItem(item)
      go(href)
    },
    [hrefFor, addRecentItem, go],
  )

  const createTask = useCallback(() => {
    if (!trimmed || !onCreateTask) return
    close()
    onCreateTask(trimmed)
  }, [trimmed, onCreateTask, close])

  const ask = useCallback(
    (question: string) => {
      if (!onAsk) return
      close()
      onAsk(question)
    },
    [onAsk, close],
  )

  const enterTextMode = useCallback(() => {
    setTextMode(true)
    setActiveValue('')
    inputRef.current?.focus()
  }, [])

  const taskById = useMemo(() => new Map(tasks.map((task) => [task.id, task])), [tasks])
  const resolveTask = useCallback(
    (taskId: string): TextTarget | null => {
      const task = taskById.get(taskId)
      return task && workspaceSlug ? { title: task.name, href: taskHref(workspaceSlug, task.id) } : null
    },
    [taskById, workspaceSlug],
  )
  const resolvePage = useCallback(
    (pageId: string): TextTarget | null => {
      const page = pages.find((candidate) => candidate.id === pageId && !candidate.isDeleted)
      const project = page?.projectId ? projectMap.get(page.projectId) : undefined
      return page && project && workspaceSlug
        ? { title: page.title || '—', href: pageHref(workspaceSlug, project, page.slug) }
        : null
    },
    [pages, projectMap, workspaceSlug],
  )

  // Every selectable result by its cmdk value, for the preview, ⌘C and →.
  const itemByValue = useMemo(() => {
    const map = new Map<string, SearchItem>()
    for (const group of groups) {
      for (const { item } of group.items) map.set(`${item.type}:${item.id}`, item)
    }
    for (const item of recentItems) map.set(`recent:${item.type}:${item.id}`, item)
    return map
  }, [groups, recentItems])
  const activeItem = itemByValue.get(activeValue) ?? null

  const cycleFilter = (direction: 1 | -1) => {
    const index = FILTERS.indexOf(activeFilter)
    const next = FILTERS[(index + direction + FILTERS.length) % FILTERS.length] ?? 'all'
    setActiveFilter(next)
    setActiveValue('')
  }

  const copyLink = async (item: SearchItem) => {
    const href = hrefFor(item)
    if (!href) return
    try {
      await navigator.clipboard.writeText(`${window.location.origin}${href}`)
      toast.success(t('search.actions.linkCopied'))
    } catch {
      // Clipboard blocked (permissions or insecure context); nothing to do.
    }
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.nativeEvent.isComposing) return
    const mod = event.metaKey || event.ctrlKey
    const input = inputRef.current

    if (event.key === 'Tab' && hasQuery && !textMode) {
      event.preventDefault()
      cycleFilter(event.shiftKey ? -1 : 1)
      return
    }
    if (event.key === 'Enter' && mod && hasQuery) {
      event.preventDefault()
      createTask()
      return
    }
    const hasSelection = !!input && input.selectionStart !== input.selectionEnd
    if (mod && event.key.toLowerCase() === 'c' && activeItem && !hasSelection) {
      event.preventDefault()
      void copyLink(activeItem)
      return
    }
    const caretAtEnd = !!input && input.selectionStart === input.value.length
    if (event.key === 'ArrowRight' && caretAtEnd && activeItem?.type === 'project') {
      event.preventDefault()
      setScope({
        kind: 'project',
        id: activeItem.id,
        name: activeItem.name,
        icon: activeItem.icon,
        iconType: activeItem.iconType,
        iconColor: activeItem.iconColor,
      })
      setQuery('')
      setActiveFilter('all')
      return
    }
    // Backspace on an empty box peels off the mode, then the scope.
    if (event.key === 'Backspace' && query === '') {
      if (textMode) {
        event.preventDefault()
        setTextMode(false)
      } else if (scope) {
        event.preventDefault()
        setScope(null)
      }
    }
  }

  const modEnter = getPlatformShortcutKeys('↵').join('')
  const showResults = hasQuery && !textMode
  const hasTitleHits = counts.all > 0
  const noTitleHits = showResults && isSettled && !hasTitleHits
  const showPreview = showResults && hasTitleHits && !!activeItem
  const isWide = showResults && hasTitleHits

  const rowRight = (item: SearchItem): string => {
    switch (item.type) {
      case 'task':
        return item.projectName ?? t('search.preview.inbox')
      case 'project':
        if (!item.parentProjectId) return t('search.preview.space')
        return scope?.kind === 'space' && item.spaceId === scope.id ? '' : (item.spaceName ?? '')
      case 'page':
        return [item.spaceName, item.projectName !== item.spaceName ? item.projectName : null]
          .filter(Boolean)
          .join(' / ')
      case 'note':
        return item.noteType === 'weekly' ? t('search.preview.weeklyNote') : t('search.preview.dailyNote')
    }
  }

  const renderItemRow = (item: SearchItem, value: string, highlight: string) => (
    <CommandItem
      key={value}
      value={value}
      data-testid="search-result"
      onSelect={() => openItem(item)}
      className={SEARCH_ROW_CLASS}
    >
      <SearchItemIcon item={item} />
      {item.type === 'task' && item.identifier && (
        <span className="w-[64px] shrink-0 truncate font-mono text-[11px] text-muted-foreground">
          {item.identifier}
        </span>
      )}
      <span className="min-w-0 flex-1 truncate">
        <HighlightedText text={item.name || '—'} query={highlight} />
      </span>
      <span className="max-w-[45%] shrink-0 truncate text-xs text-muted-foreground phone:hidden">
        {rowRight(item)}
      </span>
    </CommandItem>
  )

  const actionRow = (
    value: string,
    Icon: ComponentType<{ className?: string }>,
    label: string,
    onSelect: () => void,
    shortcut?: string,
  ) => (
    <CommandItem key={value} value={value} onSelect={onSelect} className={SEARCH_ROW_CLASS}>
      <Icon className="size-4 shrink-0 text-muted-foreground" />
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {shortcut && <Kbd>{shortcut}</Kbd>}
    </CommandItem>
  )

  const goToRows = (filter = '') =>
    GO_TO_PLACES.filter((place) =>
      t(`search.goTo.${place.key}`).toLowerCase().includes(filter),
    ).map((place) =>
      actionRow(
        `goto:${place.key}`,
        place.Icon,
        filter ? t('search.commands.goTo', { place: t(`search.goTo.${place.key}`) }) : t(`search.goTo.${place.key}`),
        () => workspaceSlug && go(`/${workspaceSlug}/${place.path}`),
        place.shortcut ? `G ${place.shortcut}` : undefined,
      ),
    )

  const scopeChip = textMode ? (
    <button
      type="button"
      onClick={() => setTextMode(false)}
      aria-label={t('search.scope.clearText')}
      className="flex h-6 shrink-0 items-center gap-1.5 rounded-md bg-background px-2 text-xs text-foreground hover:bg-accent"
    >
      <MessageSquareText className="size-3.5 text-muted-foreground" />
      {t('search.scope.text')}
      <X className="size-3 text-muted-foreground" />
    </button>
  ) : scope ? (
    <button
      type="button"
      data-testid="search-scope"
      onClick={() => setScope(null)}
      aria-label={t('search.scope.clear')}
      title={t('search.scope.clear')}
      className="flex h-6 max-w-[200px] shrink-0 items-center gap-1.5 rounded-md bg-background px-2 text-xs text-foreground hover:bg-accent"
    >
      <ProjectIcon
        variant="badge"
        iconType={scope.iconType}
        icon={scope.icon}
        iconColor={scope.iconColor}
        fallbackLabel={scope.name}
        size={8}
      />
      <span className="truncate">{scope.name}</span>
      <X className="size-3 shrink-0 text-muted-foreground" />
    </button>
  ) : null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="search-dialog"
        showCloseButton={false}
        className={cn(
          'top-[12vh] max-w-[calc(100vw-2rem)] translate-y-0 gap-0 overflow-hidden rounded-[14px] border-0 bg-card p-0 shadow-[0_40px_80px_-20px_rgb(31_29_26/0.45),0_0_0_1px_rgb(31_29_26/0.06)] dark:shadow-[0_40px_80px_-20px_rgb(0_0_0/0.7),0_0_0_1px_rgb(255_255_255/0.06)]',
          isWide ? 'sm:max-w-[600px] lg:max-w-[880px]' : 'sm:max-w-[600px]',
          // Phones (design 36d): the whole screen above the keyboard.
          'phone:inset-x-0 phone:top-0 phone:flex phone:h-[calc(100dvh-var(--kb,0px))] phone:max-w-none! phone:translate-x-0 phone:flex-col phone:rounded-none phone:pt-[env(safe-area-inset-top)] phone:shadow-none',
        )}
      >
        <DialogHeader className="sr-only">
          <DialogTitle>{t('search.title')}</DialogTitle>
          <DialogDescription>{t('search.description')}</DialogDescription>
        </DialogHeader>
        <Command
          shouldFilter={false}
          loop
          value={activeValue}
          onValueChange={setActiveValue}
          onKeyDown={handleKeyDown}
          className="rounded-none bg-transparent phone:flex phone:min-h-0 phone:flex-1 phone:flex-col"
        >
          <div className="flex h-[54px] items-center gap-2.5 border-b border-border px-4 phone:h-14">
            <Search className="size-4 shrink-0 text-muted-foreground" />
            <CommandPrimitive.Input
              ref={inputRef}
              value={query}
              onValueChange={(value) => {
                setQuery(value)
                setActiveValue('')
              }}
              placeholder={
                isCommandMode ? t('search.commandPlaceholder') : t('search.placeholder')
              }
              className="h-full min-w-0 flex-1 bg-transparent text-[16px] text-foreground outline-hidden placeholder:text-muted-foreground"
            />
            {scopeChip}
            <Kbd className="hidden sm:inline-flex">esc</Kbd>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="hidden h-10 shrink-0 px-1 text-[15px] text-foreground phone:inline"
            >
              {t('actions.cancel')}
            </button>
          </div>

          {showResults && !noTitleHits && (
            <div className="flex items-center gap-1 overflow-x-auto border-b border-border px-2.5 py-1.5 text-[12.5px]">
              {FILTERS.map((filter) => (
                <button
                  key={filter}
                  type="button"
                  onClick={() => {
                    setActiveFilter(filter)
                    setActiveValue('')
                  }}
                  data-active={activeFilter === filter}
                  className={cn(
                    'flex h-7 shrink-0 items-center gap-1.5 rounded-md px-2.5 transition-colors',
                    activeFilter === filter
                      ? 'bg-primary font-semibold text-primary-foreground'
                      : 'text-secondary-foreground hover:bg-accent',
                  )}
                >
                  {t(`search.filters.${filter}`)}
                  <span className={cn('text-[11px]', activeFilter === filter ? 'opacity-70' : 'text-muted-foreground')}>
                    {counts[filter]}
                  </span>
                </button>
              ))}
              {aiAvailable && onAsk && (
                <>
                  <span className="mx-1.5 h-4 w-px shrink-0 bg-border" aria-hidden />
                  <button
                    type="button"
                    onClick={() => ask(trimmed)}
                    className="flex h-7 shrink-0 items-center gap-1.5 rounded-md px-2.5 text-secondary-foreground hover:bg-accent"
                  >
                    <Sparkles className="size-3.5" />
                    {t('search.ask')}
                  </button>
                </>
              )}
              <span className="flex-1" />
              <span className="hidden shrink-0 items-center gap-1.5 text-xs text-muted-foreground sm:flex">
                <Kbd>tab</Kbd>
                {t('search.nextFilter')}
              </span>
            </div>
          )}

          <div className="flex min-h-0 phone:flex-1">
            <CommandList className="max-h-[min(460px,60vh)] min-w-0 flex-1 py-1.5 phone:max-h-none">
              {/* Nothing typed: recent, then places (29c). */}
              {!hasQuery && !isCommandMode && !textMode && (
                <>
                  {recentItems.length > 0 && (
                    <CommandGroup className={SEARCH_GROUP_CLASS} heading={t('search.recentlyViewed')}>
                      {recentItems
                        .slice(0, RECENT_LIMIT)
                        .map((item) => renderItemRow(item, `recent:${item.type}:${item.id}`, ''))}
                    </CommandGroup>
                  )}
                  <CommandGroup className={SEARCH_GROUP_CLASS} heading={t('search.groups.goTo')}>
                    {goToRows().slice(0, 4)}
                  </CommandGroup>
                </>
              )}

              {/* ">" lists commands. */}
              {isCommandMode && (
                <CommandGroup className={SEARCH_GROUP_CLASS} heading={t('search.groups.commands')}>
                  {onCreateTask &&
                    t('search.commands.newTask').toLowerCase().includes(commandQuery) &&
                    actionRow('cmd:new-task', Plus, t('search.commands.newTask'), () => {
                      close()
                      onCreateTask('')
                    }, 'C')}
                  {aiAvailable &&
                    onAsk &&
                    t('search.commands.ask').toLowerCase().includes(commandQuery) &&
                    actionRow('cmd:ask', Sparkles, t('search.commands.ask'), () => ask(''))}
                  {goToRows(commandQuery)}
                </CommandGroup>
              )}

              {showResults && filterTarget && (
                <FilterThisViewGroup query={query} target={filterTarget} onDone={close} />
              )}

              {showResults &&
                groups.map((group) => (
                  <CommandGroup
                    key={group.type}
                    className={SEARCH_GROUP_CLASS}
                    heading={t(`search.groups.${TYPE_FILTER[group.type]}`)}
                  >
                    {group.items.map(({ item }) =>
                      renderItemRow(item, `${item.type}:${item.id}`, trimmed),
                    )}
                    {group.total > group.items.length &&
                      activeFilter === 'all' && (
                        <CommandItem
                          value={`more:${group.type}`}
                          onSelect={() => {
                            setActiveFilter(TYPE_FILTER[group.type])
                            setActiveValue('')
                          }}
                          className={cn(SEARCH_ROW_CLASS, 'min-h-8 text-[12.5px] text-muted-foreground')}
                        >
                          {t(`search.more.${TYPE_FILTER[group.type]}`, {
                            count: group.total - group.items.length,
                          })}
                          <ArrowRight className="size-3.5" />
                        </CommandItem>
                      )}
                  </CommandGroup>
                ))}

              {/* No title matches: say where we looked and what to try (29c). */}
              {(noTitleHits || (textMode && hasQuery && textCount === 0)) && (
                <div className="px-4 pb-2 pt-3" data-testid="search-empty">
                  <p className="text-[13.5px] font-semibold text-foreground">
                    {t('search.empty.title', { query: trimmed })}
                  </p>
                  <p className="mt-0.5 text-[12.5px] text-muted-foreground">
                    {textMode
                      ? t('search.empty.text', { query: trimmed })
                      : scope
                        ? t('search.empty.scoped', { scope: scopeLabel })
                        : t('search.empty.unscoped')}
                  </p>
                </div>
              )}

              {textMode && hasQuery && (
                <SearchTextResults
                  query={trimmed}
                  workspaceSlug={workspaceSlug}
                  aiAvailable={aiAvailable}
                  resolveTask={resolveTask}
                  resolvePage={resolvePage}
                  onOpen={go}
                  onCountChange={setTextCount}
                />
              )}

              {/* Actions come last, so ↵ never creates by accident. */}
              {hasQuery && (isSettled || textMode) && (
                <>
                  {hasTitleHits && !textMode && <CommandSeparator className="mx-3 my-1.5" />}
                  <CommandGroup className={SEARCH_GROUP_CLASS}>
                    {onCreateTask &&
                      actionRow(
                        'action:create',
                        Plus,
                        t('search.actions.createTask', { query: trimmed }),
                        createTask,
                        modEnter,
                      )}
                    {!textMode && noTitleHits && scope &&
                      actionRow('action:all-spaces', Globe, t('search.actions.searchAllSpaces'), () =>
                        setScope(null),
                      )}
                    {!textMode &&
                      actionRow(
                        'action:text',
                        MessageSquareText,
                        noTitleHits
                          ? t('search.actions.searchText')
                          : t('search.actions.searchComments', { query: trimmed }),
                        enterTextMode,
                      )}
                    {aiAvailable &&
                      onAsk &&
                      (noTitleHits || textMode) &&
                      actionRow('action:ask', Sparkles, t('search.actions.ask', { query: trimmed }), () =>
                        ask(trimmed),
                      )}
                  </CommandGroup>
                </>
              )}
            </CommandList>

            {showPreview && (
              <div className="hidden max-h-[min(460px,60vh)] lg:flex">
                <SearchPreview
                  item={activeItem}
                  tasks={tasks}
                  projectMap={projectMap}
                  statusMap={statusMap}
                />
              </div>
            )}
          </div>

          <div className="hidden items-center gap-3 border-t border-border bg-chrome px-4 py-2 text-xs text-muted-foreground sm:flex">
            <span className="flex items-center gap-1">
              <Kbd>↑</Kbd>
              <Kbd>↓</Kbd>
              <span className="ml-0.5">{t('search.footer.move')}</span>
            </span>
            <span className="flex items-center gap-1.5">
              <Kbd>↵</Kbd>
              {t('search.footer.open')}
            </span>
            {onCreateTask && (
              <span className="flex items-center gap-1.5">
                <Kbd>{modEnter}</Kbd>
                {t('search.footer.create')}
              </span>
            )}
            <span className="flex-1" />
            <span className="flex items-center gap-1.5">
              {t('search.footer.type')}
              <Kbd>&gt;</Kbd>
              {t('search.footer.forCommands')}
            </span>
          </div>
        </Command>
      </DialogContent>
    </Dialog>
  )
}
